import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { t } from "../utils/i18n";
import { PlayCircle, Rocket, Megaphone, TrendingUp, FileText } from "lucide-react";
import AdCreativeUpload from "../components/AdCreativeUpload";
import AdPerformance from "../components/AdPerformance";
import AdBilling from "../components/AdBilling";

type SubView = "menu" | "performance" | "billing";

// Ex-doré (#b8952e), retiré au profit du gris uppercase déjà utilisé partout ailleurs pour un
// eyebrow de section (SectionTitle de Settings.tsx, LegalLayout.tsx) — jamais de couleur de
// marque à part entière pour un simple libellé de section, cohérent avec le noir/gris/blanc.
const sectionEyebrow: React.CSSProperties = { fontSize: 11, fontWeight: 800, color: "#999", letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 4 };
const sectionSub: React.CSSProperties = { fontSize: 12.5, color: "#999", marginBottom: 14 };
const row: React.CSSProperties = { display: "flex", alignItems: "center", gap: 12, backgroundColor: "white", borderRadius: 14, padding: "14px 16px", marginBottom: 10, border: "1px solid #F0F0F0", cursor: "pointer" };

export default function AdTool() {
  const navigate = useNavigate();
  const { lang } = useApp();
  const [sub, setSub] = useState<SubView>("menu");
  const [showUpload, setShowUpload] = useState(false);

  if (sub === "performance" || sub === "billing") {
    return (
      <div style={{ maxWidth: 640, margin: "0 auto", padding: "20px 20px 40px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
          <button onClick={() => setSub("menu")} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 20, padding: 0 }}>←</button>
          <h1 style={{ fontSize: 20, fontWeight: 800, margin: 0 }}>{sub === "performance" ? t(lang, "ad_tool_performance") : t(lang, "ad_tool_billing")}</h1>
        </div>
        {sub === "performance" ? <AdPerformance /> : <AdBilling />}
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 640, margin: "0 auto", padding: "20px 20px 40px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 28 }}>
        <button onClick={() => navigate(-1)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 20, padding: 0 }}>←</button>
        <h1 style={{ fontSize: 20, fontWeight: 800, margin: 0 }}>{t(lang, "side_menu_ad_tool")}</h1>
      </div>
      {/* 1. Diffuser une publicité */}
      <div style={sectionEyebrow}>1. {t(lang, "ad_tool_diffuse_section")}</div>
      <div style={sectionSub}>{t(lang, "ad_tool_diffuse_sub")}</div>
      <button onClick={() => setShowUpload(true)} style={{ width: "100%", display: "flex", flexDirection: "column", alignItems: "center", gap: 8, padding: "32px 20px", borderRadius: 18, border: "1px solid #F0F0F0", backgroundColor: "white", cursor: "pointer", marginBottom: 24 }}>
        <PlayCircle size={30} color="#0a0a0a" strokeWidth={1.5} />
        <span style={{ fontSize: 15, fontWeight: 800 }}>{t(lang, "ad_tool_create_ad_cta")}</span>
        <span style={{ fontSize: 12.5, color: "#999", textAlign: "center" }}>{t(lang, "ad_tool_create_ad_title")} {t(lang, "ad_tool_create_ad_desc")}</span>
      </button>

      {/* 2. Booster du contenu */}
      <div style={sectionEyebrow}>2. {t(lang, "ad_tool_boost_section")}</div>
      <div style={sectionSub}>{t(lang, "ad_tool_boost_sub")}</div>
      <div style={{ width: "100%", display: "flex", flexDirection: "column", alignItems: "center", gap: 8, padding: "32px 20px", borderRadius: 18, border: "1px solid #F0F0F0", backgroundColor: "white", marginBottom: 24 }}>
        <Rocket size={30} color="#0a0a0a" strokeWidth={1.5} />
        <span style={{ fontSize: 15, fontWeight: 800 }}>{t(lang, "ad_tool_boost_cta")}</span>
        <span style={{ fontSize: 12.5, color: "#999", textAlign: "center" }}>{t(lang, "ad_tool_boost_cta_desc")}</span>
      </div>

      {/* Publicités */}
      <div style={sectionEyebrow}>{t(lang, "ad_tool_ads_section")}</div>
      <div onClick={() => navigate("/my-sponsorships")} style={row}>
        <Megaphone size={18} color="#0a0a0a" strokeWidth={1.8} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 700 }}>{t(lang, "ad_tool_my_ads")}</div>
          <div style={{ fontSize: 12, color: "#999" }}>{t(lang, "ad_tool_my_ads_sub")}</div>
        </div>
      </div>
      <div onClick={() => setSub("performance")} style={row}>
        <TrendingUp size={18} color="#0a0a0a" strokeWidth={1.8} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 700 }}>{t(lang, "ad_tool_performance")}</div>
          <div style={{ fontSize: 12, color: "#999" }}>{t(lang, "ad_tool_performance_sub")}</div>
        </div>
      </div>
      <div onClick={() => setSub("billing")} style={row}>
        <FileText size={18} color="#0a0a0a" strokeWidth={1.8} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 700 }}>{t(lang, "ad_tool_billing")}</div>
          <div style={{ fontSize: 12, color: "#999" }}>{t(lang, "ad_tool_billing_sub")}</div>
        </div>
      </div>

      {showUpload && <AdCreativeUpload onClose={() => setShowUpload(false)} />}
    </div>
  );
}
